import Vue from 'vue'
import axios from 'axios'
import { getLocale } from '../i18n'
import store from '../store/index'

/**
 * Утилита для работы с Firebase Cloud Messaging (FCM) push-уведомлениями
 * Отвечает за инициализацию, подписку и отмену подписки на уведомления
 */
const api = {
  /**
   * Инициализация FCM, запрос разрешений и установка обработчиков событий
   */
  init() {
    // Обработчик получения сообщений
    window.FirebasePlugin.onMessageReceived((message) => {
      console.debug('FCM(device): onMessageReceived', message)

      // Обработка тапа по уведомлению
      if (message?.tap && message.url) {
        Vue.prototype.$openLink(message.url, '_system')
      }

      // Сброс счетчика уведомлений для iOS
      if (window.device.platform === 'iOS') {
        window.FirebasePlugin.setBadgeNumber(0)
      }
    }, (error) => {
      console.error('FCM(device): Error in onMessageReceived:', error)
    })

    // Проверка наличия разрешений для push-уведомлений
    window.FirebasePlugin.hasPermission((hasPermission) => {
      if (hasPermission) {
        this._setupTokenRefresh()
      } else {
        this._requestPermission()
      }
    })

    // Создание канала уведомлений для Android (для кастомных звуков)
    this._createAndroidChannel()
  },

  /**
   * Настройка обновления токена FCM
   * @private
   */
  _setupTokenRefresh() {
    window.FirebasePlugin.onTokenRefresh((token) => {
      console.debug('FCM(device): onTokenRefresh:', token)
      this.subscribe(token)
    }, (error) => {
      console.error('FCM(device): Error in onTokenRefresh:', error)
    })
  },

  /**
   * Запрос разрешения на push-уведомления
   * @private
   */
  _requestPermission() {
    window.FirebasePlugin.grantPermission((granted) => {
      console.debug(`FCM(device): Notifications permission was ${granted ? 'granted' : 'denied'}`)

      if (granted) {
        store.dispatch('analytics/logEvent', { eventName: 're_push_notifications_approved' })
        this._setupTokenRefresh()
      } else {
        store.dispatch('analytics/logEvent', { eventName: 're_push_notifications_rejected' })
      }
    })
  },

  /**
   * Создание канала уведомлений для Android
   * @private
   */
  _createAndroidChannel() {
    if (window.device.platform === 'Android') {
      window.FirebasePlugin.createChannel(
        {
          id: 'shift_closed',
          sound: 'shift_closed',
          name: 'shift_closed',
        },
        () => console.debug('FCM(device): Channel created: shift_closed'),
        (error) => console.debug(`FCM(device): Create channel error: ${error}`),
      )
    }
  },

  /**
   * Подписка на push-уведомления
   * @param {string} token - FCM токен устройства
   */
  async subscribe(token) {
    try {
      const { uuid } = window.device
      const { bearerToken, userUid } = await store.dispatch('auth/getBearerToken')

      const config = {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          'x-user-uid': userUid,
        },
      }

      // Формирование URL API
      const baseUrl = import.meta.env.VITE_API_ROOT_URL || ''
      const url = `${baseUrl}/notification/users/tokens`

      // Данные для отправки
      const data = {
        deviceId: uuid,
        token,
        lang: getLocale().toUpperCase(),
        detailedInfo: this.getDetailedInfo(),
        deviceType: 'MOBILE',
      }

      await axios.post(url, data, config)
      console.debug(`FCM(device): Subscribed for push notifications deviceId: ${uuid}`)
    } catch (error) {
      console.debug('FCM(device): Failed to subscribe:', error)
    }
  },

  /**
   * Отмена подписки на push-уведомления
   * @returns {Promise} Промис, который разрешается после отписки
   */
  async unsubscribe() {
    try {
      const { uuid } = window.device
      const { bearerToken, userUid } = await store.dispatch('auth/getBearerToken')
      const config = {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          'x-user-uid': userUid,
        },
      }

      // Формирование URL API
      const baseUrl = import.meta.env.VITE_API_ROOT_URL || ''
      const url = `${baseUrl}/notification/users/device-ids/${uuid}`

      await axios.delete(url, config)
      console.debug(`FCM(device): Unsubscribed for push notifications deviceId: ${uuid}`)
    } catch (error) {
      console.debug('FCM(device): Failed to unsubscribe:', error)
    }
  },

  /**
   * Получение детальной информации об устройстве
   * @returns {Object} Объект с информацией об устройстве и версии сборки
   */
  getDetailedInfo() {
    const info = {}
    try {
      info.cordovaBuildVersion = store.state.tools.cordovaBuildVersion
      info.device = {
        model: window.device.model,
        platform: window.device.platform,
        version: window.device.version,
        manufacturer: window.device.manufacturer,
      }
    } catch (error) {
      console.debug('FCM(device): Error getting device info:', error)
    }
    return info
  },
}

export default api
